import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { ppcHooks } from '../../hooks/useMasters.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Modal from '../../components/ui/Modal.jsx';
import ConfirmDialog from '../../components/ui/ConfirmDialog.jsx';

const schema = z.object({
  name: z.string().min(1, 'Required'),
  address: z.string().optional(),
  contactPerson: z.string().optional(),
  contactPhone: z.string().optional(),
});

export default function PPCsPage() {
  const { data: ppcs = [], isLoading } = ppcHooks.useList({ includeInactive: 'true' });
  const createMut = ppcHooks.useCreate();
  const updateMut = ppcHooks.useUpdate();
  const deleteMut = ppcHooks.useDelete();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({ resolver: zodResolver(schema) });

  const openCreate = () => { setEditing(null); reset({}); setModalOpen(true); };
  const openEdit = (p) => { setEditing(p); reset(p); setModalOpen(true); };
  const onSubmit = async (values) => {
    if (editing) await updateMut.mutateAsync({ id: editing.id, ...values });
    else await createMut.mutateAsync(values);
    setModalOpen(false); reset();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Third-Party PPCs</h2>
          <p className="text-sm text-gray-500">Pre-processing centres for raw material</p>
        </div>
        <Button icon={Plus} onClick={openCreate}>Add PPC</Button>
      </div>
      <Card>
        {isLoading ? <div className="p-8 text-center text-gray-400">Loading…</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>{['Name','Address','Contact Person','Phone','Status',''].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase tracking-wide">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ppcs.map(p => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{p.name}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{p.address || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{p.contactPerson || '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{p.contactPhone || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={"badge " + (p.isActive ? 'badge-approved' : 'badge-cancelled')}>
                        {p.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={() => openEdit(p)} className="text-gray-400 hover:text-blue-600"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => setDeleteTarget(p)} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit PPC' : 'Add PPC'}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input label="Name" required error={errors.name?.message} {...register('name')} />
          <Input label="Address" {...register('address')} />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Contact Person" {...register('contactPerson')} />
            <Input label="Contact Phone" {...register('contactPhone')} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={isSubmitting}>{editing ? 'Save Changes' : 'Add PPC'}</Button>
          </div>
        </form>
      </Modal>
      <ConfirmDialog
        open={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        onConfirm={async () => { await deleteMut.mutateAsync(deleteTarget.id); setDeleteTarget(null); }}
        loading={deleteMut.isPending} title="Delete PPC"
        message={`Delete "${deleteTarget?.name}"?`} confirmLabel="Delete"
      />
    </div>
  );
}
